import { module, Mutations } from './helper';
export { default as settingsHelper, Mutations } from './helper';
import { ThemeEnum, Theme } from '@/types/theme';

import { Settings, I18N, SignupField, ISignupPage, DataProvider } from '@/api/signup/interface';
import signup from '@/api/signup';
import Vue from 'vue';
import { toCssVariables } from '@/util/theme';
import i18n from '@/i18n';

export default {
    namespaced: true,
    state: { 
        theme: undefined,
        showWelcomePage: true,
        client: {
            brand: undefined,
            name: undefined,
            logo: undefined,
            logoSmall: undefined,
        },
        access: {
            dataType: '',
            frequency: '',
            period: '',
            reason: '',
            daysOfTransactions: -1,
        },
        privacyPolicy: undefined,
        termsConditions: undefined,
        privacyNotice: undefined,
        inputs: {},
        textOverrides: {},
        i18n: {
            available: undefined,
            current: undefined,
        },
        config: undefined,
        clientConfigs: {},
    },
    getters: {
        allowedProviders: (s) => (s.config && s.config.provider && s.config.provider.allowedProviders) || [],
        disableNewTab: (s) => s.config && s.config.provider && s.config.provider.disableNewTab,
        reverseLookUpRequestWaitTimeInSec: (s) => s.config && s.config.provider && s.config.provider.reverseLookUpRequestWaitTimeInSec,
        applyPlusSignUp: (s) => s.config && s.config.provider && s.config.provider.applyPlusSignUp,
        allowChangeProvider: (s) => s.config && s.config.provider && s.config.provider.allowChangeProvider,
        allowDownloadPdf: (s) => s.config && s.config.complete && s.config.complete.allowDownloadPdf,
        providers: (s, g) => ({
            openBanking: g.allowedProviders.indexOf(DataProvider.OpenBanking) >= 0, 
            yodlee: g.allowedProviders.find(
                (x) => x === DataProvider.Yodlee || x === DataProvider.YodleePdf,
            ),
            starling: g.allowedProviders.indexOf(DataProvider.Starling) >= 0,
            trueLayer: g.allowedProviders.indexOf(DataProvider.TrueLayer) >= 0
        }),
        brand: (s) => s.client.brand,
        client: (s) => s.client.name,
        logo: (s) => s.client.logo,
        logoXs: (s) => s.client.logoSmall,
        accessFrequency: (s) => s.access.frequency,
        accessReason: (s) => s.access.reason,
        accessPeriod: (s) => s.access.period,
        accessDataType: (s) => s.access.dataType,
        daysOfTransactions: (s) => s.access.daysOfTransactions,
        privacyPolicy: (s) => s.privacyPolicy || 'StandardPrivacyPolicyOB',
        termsConditions: (s) => s.termsConditions || 'StandardTermsOB',
        privacyNotice: (s) => s.privacyNotice || 'PrivacyNotice',
        allowAddBank: (s) => s.config && s.config.complete && s.config.complete.allowAddBank,
        allowClose: (s) => s.config && s.config.complete && s.config.complete.allowClose,
        autoRedirect: (s) => s.config && s.config.complete && s.config.complete.autoRedirect,
        showSuccessIcon: (s) => s.config && s.config.complete && s.config.complete.showSuccessIcon !== false,
        showSuccessSubTitle: (s) => s.config && s.config.complete && s.config.complete.showSuccessSubTitle !== false,
        locale: (s) => ({
            available: s.i18n.available,
            current: s.i18n.current,
        }),
        consentTermPrivacyCheckbox: (s) => s.consentTermPrivacyCheckbox,
        pages: (s) => (s.config && s.config.pages) || [],
        textOverrides: (s) => s.textOverrides,
        footer: (s) => s.config && s.config.footer && s.config.footer,
        themeStyle: (s) => toCssVariables(s.theme),
        config: (s) => s.config,
        clientConfigs: (s) => s.clientConfigs,
        termsAndConditionPage: (s, g, rs, rg) => {
            const locations = (s.config && s.config.app && s.config.app.termsConditions && s.config.app.termsConditions.location) || [];
            const pages = rs.navigation.pages as string[];
            if (pages) {
                for (const p of locations) {
                    if (pages.indexOf(p) >= 0) {
                        return p;
                    }
                }
            }
            return undefined;
        },
        noHeader: (state: any) => state.config.app.noHeader,
        noBurgerMenu: (state: any) => state.config.app.noBurgerMenu,
        noFooter: (state: any) => state.config.app.noFooter,        
        showTips: (state: any) => state.config.app.showTips,
        errorAutoRedirectEnabled: (s) => s.config && s.config.provider && s.config.provider.errorAutoRedirectEnabled,
        errorRedirectUrl: (s) => s.config && s.config.provider && s.config.provider.errorRedirectUrl,
        thirdPartyIntegrationApp: (s) => s.config && s.config.provider && s.config.provider.thirdPartyIntegrationApp,
        earlyRedirectEnabled: (s) => s.config && s.config.provider && s.config.provider.earlyRedirectEnabled,
        backButton: (s) => (s.config && s.config.provider && s.config.provider.backButton) || `Return to ${s.client.name}`,
        backButtonUrl: (s) => s.config && s.config.provider && s.config.provider.backButtonUrl,
        // Journey flags now come from the persisted token (via config.provider) instead of URL query
        // markers, so they survive refreshes and mirror the completeUrl precedence resolved on the backend.
        isIframeJourney: (s) => !!(s.config && s.config.provider && s.config.provider.isIframeJourney),
        isBacktoClientEnabled: (s) => !!(s.config && s.config.provider && s.config.provider.isBacktoClientEnabled)
    },
    actions: {
        async init({ commit }) {
            const [settings, appConfig] = await Promise.all([
                signup.getSettings(),
                signup.getAppConfig(),
            ]);
            const { company, logo, logoSmall, textOverrides, brand } = settings;
            commit(Mutations.UPDATE_THEME, settings.theme);
            commit(Mutations.UPDATE_CLIENT, { name: company, logo, logoSmall, brand });
            commit(Mutations.UPDATE_TEXT_OVERRIDES, textOverrides);
            commit(Mutations.UPDATE_APP_CONFIG, appConfig);

            // TODO remove the UPDATE_EVERYTHING
            commit(Mutations.UPDATE_EVERYTHING_TEMP, settings);
            const locale = (appConfig.app && appConfig.app.defaultLocale) || 'en-GB';
            commit(Mutations.UPDATE_LOCALE, { current: locale });
            i18n.locale = locale;
        },
        setTheme({ commit }, payload) {
            commit(Mutations.UPDATE_THEME, payload);
        },
        setClientConfigs({ state, commit }, clientConfigs) {
            commit(Mutations.SET_CLIENT_CONFIG, clientConfigs);
        }
    },
    mutations: {
        [Mutations.UPDATE_THEME](state, theme: Theme) {
            state.theme = theme;
        },
        [Mutations.UPDATE_CLIENT](state, client) {
            state.client = client;
        },
        [Mutations.UPDATE_TEXT_OVERRIDES](state, overrides) {
            state.textOverrides = overrides || {};
        },
        [Mutations.UPDATE_APP_CONFIG](state, config) {
            state.config = config;
        },
        [Mutations.UPDATE_LOCALE](state, { current }: { current: string }) {
            state.i18n.available = [current]; // todo change to list
            state.i18n.current = current;
        },
        [Mutations.SET_CLIENT_CONFIG](state, clientConfigs) {
            state.clientConfigs = { ...state.clientConfigs, ...clientConfigs };
        },
        [Mutations.UPDATE_EVERYTHING_TEMP](state, settings: Settings) {
            state.client.name = settings.company;
            state.client.brand = settings.brand;
            state.client.logo = settings.logo;
            state.client.logoSmall = settings.logoSmall || settings.logo;
            state.termsConditions = settings.termsOfUse;
            state.privacyPolicy = settings.privacyPolicy;
            state.access.dataType = settings.accessDataType;
            state.access.frequency = settings.accessFrequency;
            state.access.period = settings.accessPeriod;
            state.access.reason = settings.accessReason;
            state.access.daysOfTransactions = settings.daysOfTransactions;
            state.consentTermPrivacyCheckbox = settings.consentTermPrivacyCheckbox;
            if (settings.localisations) {
                const l = [];
                for (const i of settings.localisations) {
                    if (i === I18N.EnGb) {
                        l.push('en-GB');
                    }
                    if (i === I18N.PtPt) {
                        l.push('pt-PT');
                    }
                    if (i === I18N.EnUs) {
                        l.push('en-US');
                    }
                }
                state.i18n.available = l;
                state.i18n.current = (l && l[0]) || 'en-GB';
            }
            const theme = settings.theme || {};
            for (const k of Object.keys(theme)) {
                const v: string = (theme as any)[k];
                if (v) {
                    Vue.set(state.theme as object, k, v);
                }
            }
        },
    },
} as module;
